import { FC, Fragment, createPortal } from "preact/compat";
import { Transition } from "@headlessui/react";
import { usePortalTarget } from "~/hooks";
import Ghosts from "./Ghosts";
import MenuItem from "./MenuItem";
import type { FloatingMenuProps } from "./interfaces";
import useFloatingMenu from "./useFloatingMenu";
import DragIcon from "@material-design-icons/svg/outlined/drag_indicator.svg";
import cn from "classnames";

const FloatingMenu: FC<FloatingMenuProps> = (props) => {
    const { id, items, ghost } = props;
    const {
        ref,
        location,
        isHorizontal,
        isReverse,
        isMoving,
        isHidden,
        menuHeight,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseDown,
    } = useFloatingMenu(props);

    return (
        <>
            <Transition
                show={!isHidden}
                appear
                enter="transition duration-300"
                enterFrom="scale-95 opacity-0"
                enterTo="scale-100 opacity-100"
                leave="transition duration-300"
                leaveFrom="scale-100 opacity-100"
                leaveTo="scale-95 opacity-0"
                as={Fragment}
            >
                <div
                    ref={(v) => {
                        ref.current = v;

                        if (v) {
                            v.style.left = location.peek().left ?? "";
                            v.style.top = location.peek().top ?? "";
                            v.style.right = location.peek().right ?? "";
                            v.style.bottom = location.peek().bottom ?? "";
                        }
                    }}
                    className={cn(
                        "absolute origin-bottom-left",
                        ghost ? "z-[49] opacity-80" : "z-50",
                        ghost &&
                            "rounded-[20px] ring-2 ring-sky-500 ring-offset-2 ring-offset-white",
                        isMoving.value && "pointer-events-none",
                    )}
                    data-floating-menu={id}
                    data-floating-horizontal={String(isHorizontal)}
                    data-floating-left={location.peek().left}
                    data-floating-top={location.peek().top}
                    data-floating-bottom={location.peek().bottom}
                    data-floating-right={location.peek().right}
                >
                    <div
                        className={cn(
                            "relative transition duration-100",
                            ghost && "grayscale",
                        )}
                        onMouseLeave={handleMouseLeave}
                        onMouseEnter={handleMouseEnter}
                        data-floating-content
                    >
                        <div
                            className={cn(
                                "relative z-[51] flex items-center rounded-[20px] bg-gradient-primary-to-b p-1 transition-all duration-[50ms]",
                                isHorizontal ? "flex-row" : "flex-col",
                                ghost && "pointer-events-none",
                            )}
                        >
                            {items.map((item, index) => (
                                <MenuItem
                                    key={item.link}
                                    item={item}
                                    index={index}
                                    isHorizontal={isHorizontal}
                                    isReverse={isReverse}
                                    menuHeight={menuHeight}
                                />
                            ))}
                            <div
                                className={cn(
                                    "p-1",
                                    isHorizontal ? "ml-4" : "mt-4",
                                    isMoving.value
                                        ? "cursor-grabbing"
                                        : "cursor-grab",
                                )}
                                onMouseDown={handleMouseDown}
                            >
                                <DragIcon
                                    width="18"
                                    height="18"
                                    className="pointer-events-none fill-gray-200"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
            {isMoving.value && <Ghosts id={id} items={items} />}
        </>
    );
};

// this is needed to use absolute position for the menu instead of fixed
// in order to handle overscroll behavior on mac
//
const Wrapper: FC<FloatingMenuProps> = (props) => {
    const target = usePortalTarget();

    if (!target.value) {
        return null;
    }

    return createPortal(<FloatingMenu {...props} />, target.value);
};

export default Wrapper;
